import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { useCreditSearchQuery } from "../creditSearch";
import { imageStore } from "./assets";
import { hitTestScene } from "./layout/scene";
import { paintWrapperContent } from "./paint/paintScene";

function CardFrameCanvas({ wrapper, sceneWidth, sceneHeight, sceneVersion, query }) {
  const canvasRef = useRef(null);
  const [sizeVersion, setSizeVersion] = useState(0);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || typeof ResizeObserver === "undefined") return undefined;
    const observer = new ResizeObserver(() => setSizeVersion((version) => version + 1));
    observer.observe(canvas);
    return () => observer.disconnect();
  }, []);

  useLayoutEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !wrapper) return;
    const cssWidth = Math.max(1, canvas.clientWidth);
    const cssHeight = Math.max(1, canvas.clientHeight);
    const dpr = window.devicePixelRatio || 1;
    const deviceWidth = Math.round(cssWidth * dpr);
    const deviceHeight = Math.round(cssHeight * dpr);
    if (canvas.width !== deviceWidth) canvas.width = deviceWidth;
    if (canvas.height !== deviceHeight) canvas.height = deviceHeight;
    const ctx = canvas.getContext("2d");
    ctx.setTransform(
      (dpr * cssWidth) / sceneWidth,
      0,
      0,
      (dpr * cssHeight) / sceneHeight,
      0,
      0,
    );
    ctx.clearRect(0, 0, sceneWidth, sceneHeight);
    paintWrapperContent(ctx, wrapper, { images: imageStore, query });
  }, [wrapper, sceneWidth, sceneHeight, sceneVersion, query, sizeVersion]);

  return (
    <canvas
      ref={canvasRef}
      aria-hidden="true"
      data-html2canvas-ignore="true"
      style={{
        position: "absolute",
        inset: 0,
        display: "block",
        width: "100%",
        height: "100%",
        pointerEvents: "none",
        zIndex: 1,
      }}
    />
  );
}

/**
 * One canvas per card frame. Labels, focus, the watermark, and the title
 * guide stay in the DOM so scroll-to and the existing CSS still apply.
 */
export default function CardCanvasPreview({
  scene,
  sceneVersion,
  credits,
  currentBlockIdx,
  aspectRatioStyles,
  isDisplayTitle,
  isUserPaid,
  onSelect,
}) {
  const query = useCreditSearchQuery();
  if (!scene) return null;

  const pointFromEvent = (event, host) => {
    const rect = host.getBoundingClientRect();
    if (!rect.width || !rect.height) return null;
    return {
      x: ((event.clientX - rect.left) / rect.width) * scene.width,
      y: ((event.clientY - rect.top) / rect.height) * scene.frameHeight,
    };
  };

  return scene.wrappers.map((wrapper) => {
    const idx = wrapper.idx;
    const blockName = credits?.[idx]?.options?.block_name || `Block ${idx}`;
    const isPair = credits?.[idx]?.options?.block_inset === "sidebyside";

    const handleClick = (event) => {
      const point = pointFromEvent(event, event.currentTarget);
      if (!point) return;
      const hit = hitTestScene({ wrappers: [wrapper] }, point.x, point.y);
      if (!hit?.credit) return;
      if (hit.logo && typeof window !== "undefined") {
        window.dispatchEvent(
          new CustomEvent("scrollx:logo-select", { detail: hit.logo.detail }),
        );
      }
      onSelect?.(hit.credit.id, hit.credit.idx);
    };

    const handleMouseMove = (event) => {
      const host = event.currentTarget;
      const point = pointFromEvent(event, host);
      if (!point) return;
      const hit = hitTestScene({ wrappers: [wrapper] }, point.x, point.y);
      const cursor = hit?.logo ? "pointer" : "";
      if (host.style.cursor !== cursor) host.style.cursor = cursor;
    };

    const frameBody = (
      <>
        <div className={isUserPaid ? "" : "watermark"} />
        <CardFrameCanvas
          wrapper={wrapper}
          sceneWidth={scene.width}
          sceneHeight={scene.frameHeight}
          sceneVersion={sceneVersion}
          query={query}
        />
        <div
          className={isDisplayTitle ? "displayTitle" : ""}
          data-html2canvas-ignore="true"
          aria-hidden="true"
        />
      </>
    );

    if (isPair) {
      return (
        <div className="w-full" key={`block-group-${idx}`}>
          <div>
            <div id={`Block${idx}`} className="blockName text-xl text-gray-500 text-left">
              Card {idx + 1}: {blockName}
            </div>
            <div className="aspectRatioClassStyle" style={aspectRatioStyles}>
              <div
                id={`view_${idx}`}
                data-credit-block-index={idx}
                className="blockWrapper pr-prants min-h-full h-full"
                style={{ width: "100%", height: "100%", position: "relative" }}
                onClick={handleClick}
                onMouseMove={handleMouseMove}
              >
                <div
                  className="flex flex-row gap-x-[30px] w-full"
                  style={{
                    position: "absolute",
                    inset: 0,
                    height: "100%",
                    pointerEvents: "none",
                  }}
                >
                  <div
                    className={`w-[50%] h-full ${
                      idx === currentBlockIdx ? "CardfocusDiv" : ""
                    }`}
                  />
                  <div
                    className={`w-[50%] h-full ${
                      idx + 1 === currentBlockIdx ? "CardfocusDiv" : ""
                    }`}
                  />
                </div>
                {frameBody}
              </div>
            </div>
          </div>
        </div>
      );
    }

    return (
      <div key={`block-${idx}`} className="w-full my-[50px]" style={{ width: "inherit" }}>
        <div className="overflow-hidden pb-[20px]">
          <div id={`Block${idx}`} className="blockName text-xl text-gray-500 text-left">
            Card {idx + 1}: {blockName}
          </div>
          <div
            className="flex items-center justify-center aspectRatioClassStyle"
            style={aspectRatioStyles}
          >
            <div
              id={`view_${idx}`}
              data-credit-block-index={idx}
              className={`blockWrapper pr-prants w-full min-h-full h-full ${
                idx === currentBlockIdx ? "CardfocusDiv" : ""
              }`}
              style={{ transformOrigin: "top left", height: "100%", position: "relative" }}
              onClick={handleClick}
              onMouseMove={handleMouseMove}
            >
              {frameBody}
            </div>
          </div>
        </div>
      </div>
    );
  });
}
